import { RefResolver } from "json-schema-ref-resolver";
import { Ajv } from "ajv";
import addFormats from "ajv-formats";
import { JSONSchema, MergeSchemasOptions } from "./types";
import { makeAllFieldsOptional, removeAdditionalProperties } from "./transform";

const defaultMergeOptions = {
  ignoreRequired: true,
  allowAdditionalProperties: true
};

const getSchemaId = (index: number) => `schema${index}`;

const parseSchema = (schema: string | JSONSchema): object =>
  typeof schema === "string" ? JSON.parse(schema) : schema;

const addSchemaRef = (schema: JSONSchema, index: number): object => ({
  ...schema,
  $id: getSchemaId(index)
});

const validateSchema = (schema: JSONSchema) => {
  try {
    // NOTE: I instantiate ajv every time because this function will also be used on React projects, which may
    // execute more than once. If we try to compile a schema with a well-defined id, ajv is not happy. For this reason,
    // we choose not to re-use the same ajv instance but create a new one every time
    const ajv = new Ajv();
    addFormats(ajv);
    ajv.compile(schema);
  } catch (error) {
    console.error(error);
    throw new Error("JSON Schema is not well formed");
  }
};

const applyTransformations = (
  schema: JSONSchema,
  options: MergeSchemasOptions
) => {
  console.log(options);
  const { ignoreRequired, allowAdditionalProperties } = options;
  const transformations = [];
  if (ignoreRequired) {
    transformations.push(makeAllFieldsOptional);
  }
  if (allowAdditionalProperties) {
    transformations.push(removeAdditionalProperties);
  }
  return transformations.reduce(
    (tempSchema, transform) => transform(tempSchema),
    schema
  );
};

export const mergeSchemas = (
  schemas: (JSONSchema | string)[],
  options: MergeSchemasOptions = {}
): JSONSchema => {
  const opts = { ...defaultMergeOptions, ...options };
  const sourceSchemas = schemas.map(parseSchema).map(addSchemaRef);
  sourceSchemas.every(validateSchema);
  const allOf = sourceSchemas.map((_, index) => ({ $ref: getSchemaId(index) }));

  const combinedSchemaId = "combined";
  const combinedSchema = { $id: combinedSchemaId, allOf };

  const refResolver = new RefResolver();
  refResolver.addSchema(combinedSchema);
  sourceSchemas.forEach((schema) => refResolver.addSchema(schema));
  refResolver.derefSchema(combinedSchemaId);
  const mergedSchema = refResolver.getDerefSchema(combinedSchemaId);
  return applyTransformations(mergedSchema, opts);
};
